import getOr from 'lodash/fp/getOr';
import jwt_decode from "jwt-decode";

import { REQUIRE_LOGIN_PATHS, REQUIRE_UN_LOGIN_PATHS, ROOT_GLOBAL } from '../global/root';
import { signOut } from '../utils/firebase';
import { Auth } from './auth';
import { getTierFromCode } from '../utils/utils';

// local storage and session storage
const storage = localStorage || window.localStorage;

export const LOCAL_STORAGE = {
  REDUCER_PATH: "LOCAL_STORAGE",
  LOCAL_KEY: "data",
  VALUE: "value",
  TYPE: {
    token: "t",
    fullname: "fn",
    userId: "uid",
    refreshToken: "rt",
    cookiePopup: "cp",
    walkThrought: "wt",
    flashMessage: "fm",
    localtion: "l",
    language: "hl"
  }
}

// get data from local (local variable or session storage or local storage)
export function getLocalInfo(type) {
  if (storage && storage.length) {
    return storage.getItem(type);
  } else {
    return null;
  }
}

// set local data (local variable and session storage and local storage)
export function setLocalInfo(type, value) {
  if (value !== undefined && value !== null) {
    if (storage) {
      storage.setItem(type, value);
    }
  } else {
    if (storage) {
      storage.removeItem(type);
    }
  }
}

export class LocalStorage {
  constructor(token, fullname, userId, refreshToken, cookiePopup, walkThrought, flashMessage, location, language) {
    this[LOCAL_STORAGE.TYPE.token] = token;
    this[LOCAL_STORAGE.TYPE.fullname] = fullname;
    this[LOCAL_STORAGE.TYPE.userId] = userId;
    this[LOCAL_STORAGE.TYPE.refreshToken] = refreshToken;
    this[LOCAL_STORAGE.TYPE.cookiePopup] = cookiePopup;
    this[LOCAL_STORAGE.TYPE.walkThrought] = walkThrought;
    this[LOCAL_STORAGE.TYPE.flashMessage] = flashMessage;
    this[LOCAL_STORAGE.TYPE.localtion] = location;
    this[LOCAL_STORAGE.TYPE.language] = language;
  }

  saveToLocalStorage() {
    setLocalInfo(LOCAL_STORAGE.LOCAL_KEY, JSON.stringify(this));
  }

  static fromLocalStorage(data = null) {
    let source = data;
    let result = new LocalStorage();
    if (!data) {
      source = getLocalInfo(LOCAL_STORAGE.LOCAL_KEY);
    }
    if (source && typeof (source) === "string") {
      try {
        source = JSON.parse(source);
      } catch (error) {
        source = null;
      }
    }
    if (source && typeof (source) === "object") {
      for (let key in source) {
        result[key] = source[key];
      }
    }
    return result;
  }

  static getLocalInfo(type, state = null) {
    const data = state !== null ? localStorageSelector(state) : LocalStorage.fromLocalStorage();
    return data[type];
  }

  static setLocalInfo(type, data) {
    let newData = LocalStorage.fromLocalStorage();
    newData[type] = data;
    if (typeof window.setLocal === "function") {
      window.setLocal({
        [LOCAL_STORAGE.VALUE]: newData
      })
    }
    newData.saveToLocalStorage();
  }

  static getUserToken(state = null) {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.token, state);
  }

  static isLoggedIn(state = null) {
    return !!LocalStorage.getUserToken(state);
  }

  static setUserToken(token) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.token, token);
  }

  static getUserName(state = null) {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.fullname, state);
  }

  static setUserName(user) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.fullname, user);
  }

  static getUserId(state = null) {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.userId, state);
  }

  static setUserId(userID) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.userId, userID);
  }

  static getRefreshToken() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.refreshToken);
  }

  static setRefreshToken(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.refreshToken, role);
  }

  static getFlashMessage() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.flashMessage);
  }

  static setFlashMessage(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.flashMessage, role);
  }

  static getWalkThrought() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.walkThrought);
  }

  static setWalkThrought(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.walkThrought, role);
  }

  static getCookiePopup() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.cookiePopup);
  }

  static setCookiePopup(role) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.cookiePopup, role);
  }

  static getLocation() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.localtion);
  }

  static setLocation(location) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.localtion, location);
  }

  static getLanguage() {
    return LocalStorage.getLocalInfo(LOCAL_STORAGE.TYPE.language) || "en";
  }

  static setLanguage(language) {
    LocalStorage.setLocalInfo(LOCAL_STORAGE.TYPE.language, language);
  }

  static decodeToken = (token) => {
    if (token) {
      return jwt_decode(token);
    }
    const cToken = LocalStorage.getUserToken();
    if (cToken) {
      return jwt_decode(cToken);
    } else {
      return {};
    }
  }

  // handle login
  static requireLogin(cb, state = null) {
    if (!LocalStorage.isLoggedIn(state)) {
      signOut(function () {
        window.location.replace(Auth.callBackLogin(cb));
      }, function () {
        window.location.replace(Auth.callBackLogin(cb));
      });
      return false;
    }
    return true;
  }

  // handle login
  static requireUnLogin(state = null) {
    if (LocalStorage.isLoggedIn(state)) {
      window.location.replace(ROOT_GLOBAL.USER_TRADE_ADVISORY);
      return false;
    }
    return true;
  }

  // handle login redirect
  // static requireUnLoginRedirect(cb = null, state = null) {
  //   if (LocalStorage.isLoggedIn(state)) {
  //     window.location.replace(cb || ROOT_GLOBAL.USER_TRADE_ADVISORY);
  //     return false;
  //   }
  //   return true;
  // }

  // set user data token, user info, user id, role
  static setLoginInfo(token, userName, userId, refreshToken) {
    LocalStorage.setUserToken(token);
    LocalStorage.setUserName(userName);
    LocalStorage.setUserId(userId);
    LocalStorage.setRefreshToken(refreshToken);
  }

  static logout(cb = null) {
    LocalStorage.setLoginInfo();
    if (cb) {
      window.location.href = cb;
    }
  }

  static getTierFromToken(token) {
    const data = LocalStorage.decodeToken(token);
    return getTierFromCode(data.pkg);
  }

  static getPackageExpired(token) {
    const data = LocalStorage.decodeToken(token);
    return getTierFromCode(data.pex) * 1000;
  }

  static hasTrial(token) {
    const data = LocalStorage.decodeToken(token);
    return data.tri;
  }

  static inRequireLoginPaths() {
    return REQUIRE_LOGIN_PATHS.includes(window.location.pathname);
  }

  static inRequireUnLoginPaths() {
    return REQUIRE_UN_LOGIN_PATHS.includes(window.location.pathname);
  }

  static getTokenExpired(token) {
    const data = LocalStorage.decodeToken(token);
    return data.exp * 1000;
  }
}

export const localStorageSelector = getOr(new LocalStorage(), `${LOCAL_STORAGE.REDUCER_PATH}.${LOCAL_STORAGE.VALUE}`);